/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import PaymentsService from '../../../Services/PaymentsService';
import Error from '../../../Components/Error';
import { formatDateTime } from '../../../Utils/timezone';
import { formatPaymentAmount } from '../../Payments/Components/paymentDisplay';

function RefundPaymentModal({
  show, onHide, payment, onRefunded,
}) {
  const [refundType, setRefundType] = useState('full');
  const [partialAmount, setPartialAmount] = useState('');
  const [validationError, setValidationError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Re-seed defaults whenever the modal is (re)opened for a different
  // payment. Without this, switching from one row to another would keep
  // stale form values.
  useEffect(() => {
    if (show) {
      setRefundType('full');
      setPartialAmount('');
      setValidationError('');
      setError('');
      setSubmitting(false);
    }
  }, [show, payment?._id]);

  const gross = Number(payment?.gross_amount);
  const currency = payment?.currency || 'EUR';

  // Compute validity for the partial path so the submit button can be
  // disabled inline (and we still surface a clear inline message when
  // the admin clicks anyway).
  const partialNum = partialAmount === '' ? NaN : Number(partialAmount);
  const partialInvalid = refundType === 'partial' && (
    partialAmount === ''
    || Number.isNaN(partialNum)
    || partialNum <= 0
    || (!Number.isNaN(gross) && partialNum > gross)
  );

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setValidationError('');

    if (!payment?._id) {
      setValidationError('No payment selected');
      return;
    }

    let partial;
    if (refundType === 'partial') {
      if (partialAmount === '' || Number.isNaN(partialNum)) {
        setValidationError('Enter a valid partial amount');
        return;
      }
      if (partialNum <= 0) {
        setValidationError('Partial amount must be greater than 0');
        return;
      }
      if (!Number.isNaN(gross) && partialNum > gross) {
        setValidationError(`Partial amount cannot exceed ${formatPaymentAmount(gross, currency)}`);
        return;
      }
      partial = partialNum;
    }

    setSubmitting(true);
    try {
      const offsetPayment = await PaymentsService.refundPayment(
        payment._id,
        partial,
      );
      if (onRefunded) onRefunded(offsetPayment, payment._id);
      if (onHide) onHide();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to issue refund');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    if (onHide) onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-undo me-2" />
          Issue Refund
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Original payment summary card — keeps the admin oriented
            without having to flip back to the table behind the modal. */}
        {payment && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="text-muted small mb-1">Original payment</div>
            <div className="fw-bold">
              {formatPaymentAmount(payment.gross_amount, payment.currency)}
              {' '}
              <span className="text-muted small">
                (
                {payment.currency || '—'}
                )
              </span>
            </div>
            <div className="small text-muted">
              {payment.method ? payment.method.toUpperCase() : '—'}
              {' · '}
              {payment.occurred_at ? formatDateTime(payment.occurred_at) : '—'}
              {payment.status && (
                <>
                  {' · '}
                  {payment.status}
                </>
              )}
            </div>
          </div>
        )}

        <Error error={error} setError={setError} />
        {validationError && (
          <div className="alert alert-warning py-2 mb-3 small" role="alert">
            {validationError}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="mb-2">Refund type</Form.Label>
            <Form.Check
              type="radio"
              id="refund_type_full"
              name="refund_type"
              label={`Full refund (${formatPaymentAmount(gross, currency)})`}
              checked={refundType === 'full'}
              onChange={() => setRefundType('full')}
              disabled={submitting}
            />
            <Form.Check
              type="radio"
              id="refund_type_partial"
              name="refund_type"
              label="Partial refund"
              checked={refundType === 'partial'}
              onChange={() => setRefundType('partial')}
              disabled={submitting}
            />
          </Form.Group>

          {refundType === 'partial' && (
            <Form.Group className="mb-3" controlId="rfp_partial_amount">
              <Form.Label>
                Amount to refund
                {' '}
                *
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                disabled={submitting}
                autoFocus
              />
              <Form.Text className="text-muted">
                Up to
                {' '}
                {formatPaymentAmount(gross, currency)}
              </Form.Text>
            </Form.Group>
          )}

          {refundType === 'full' && (
            <div className="alert alert-warning py-2 mb-3 small" role="alert">
              <i className="fa fa-exclamation-triangle me-1" />
              This will mark the original payment as refunded. If this was the only
              inbound payment on the booking, the booking will return to Unpaid status.
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleSubmit}
          disabled={submitting || partialInvalid}
        >
          {submitting ? 'Processing…' : 'Confirm Refund'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RefundPaymentModal;
